// src/pages/AddWaiterPage.jsx
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { API_ENDPOINTS, API_BASE_URL } from "../config/constants";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaChevronDown,
  FaEye,
  FaEyeSlash,
  FaSyncAlt,
  FaTrash,
  FaEdit,
  FaTimes,
} from "react-icons/fa";
import {
  DEFAULT_BILLING_ROLE_DISCOUNT_CAP,
  buildStaffCapPayload,
  formatDiscountPercent,
  formatStaffCapForInput,
  isBillingRole,
  normalizeStaffRoleKey,
  resolveStaffDefaultDiscountCap,
  sanitizePercentInput,
} from "../utils/staffAccess";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import {
  changeOfflineStaffPassword,
  createOfflineStaff,
  deleteOfflineStaff,
  forceLogoutOfflineStaff,
  listOfflineStaff,
  toggleOfflineStaff,
  updateOfflineStaff,
} from "../offline/staffStore";

/* ---------- Brand palette ---------- */
const COLOR = {
  primary: "var(--app-primary)",
  bg: "#f5f7fb",
  white: "#ffffff",
  line: "#e8eef5",
  text: "#1f2937",
  muted: "#6b7280",
  danger: "#dc3545",
};

/* ---------- Endpoints (same as RN) ---------- */
const STAFF_LIST_URL = `${API_BASE_URL}/admin/staff/list`;
const DELETE_STAFF_URL = `${API_BASE_URL}/admin/delete/staff`;
const TOGGLE_STATUS_URL = `${API_BASE_URL}/admin/staff/toggle-status`;
const UPDATE_STAFF_URL = `${API_BASE_URL}/admin/staff/edit`;
const CHANGE_PASSWORD_URL = `${API_BASE_URL}/admin/staff/change-password`;
const FORCE_LOGOUT_URL = `${API_BASE_URL}/admin/staff/force-logout`;

/* ---------- Role options ---------- */
const ROLE_OPTIONS = [
  { label: "Cashier", value: "cashier" },
  { label: "Waiter", value: "waiter" },
  { label: "Chef", value: "chef" },
  { label: "Attendant", value: "attendant" },
];

const normalizeStaffRow = (row = {}) => {
  const role = normalizeStaffRoleKey(row?.role);

  return {
    ...row,
    role,
    max_discount_percent: buildStaffCapPayload(
      role,
      row?.max_discount_percent
    ),
  };
};

const AddWaiterPage = () => {
  const [restaurantId, setRestaurantId] = useState(null);

  // add form
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [maxDiscountPercent, setMaxDiscountPercent] = useState("");

  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showEditPwd, setShowEditPwd] = useState(false);

  // list state
  const [loadingList, setLoadingList] = useState(true);
  const [staff, setStaff] = useState([]); // [{id, name, active, role, phone, email}]
  const [editModal, setEditModal] = useState({
    open: false,
    id: null,
    name: "",
    phone: "",
    email: "",
    role: "",
    max_discount_percent: String(DEFAULT_BILLING_ROLE_DISCOUNT_CAP),
    password: "",
  });

  // filters for list
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");

  /* ---------------- helper ---------------- */
  const gentle = (t) => {
    toast.info(t, { autoClose: 2400 });
    return false;
  };

  const validateMaxDiscountPercent = (activeRole, value) => {
    if (!isBillingRole(activeRole)) {
      return true;
    }

    if (value === "") {
      return true;
    }

    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0 || numeric > 100) {
      return gentle("Max discount must be between 0 and 100");
    }

    return true;
  };

  const updateAddRole = (nextRole) => {
    const normalizedRole = normalizeStaffRoleKey(nextRole);
    setRole(normalizedRole);
    setMaxDiscountPercent((prev) => {
      if (!isBillingRole(normalizedRole)) {
        return "";
      }

      return isBillingRole(role)
        ? prev
        : String(DEFAULT_BILLING_ROLE_DISCOUNT_CAP);
    });
  };

  const updateEditRole = (nextRole) => {
    const normalizedRole = normalizeStaffRoleKey(nextRole);
    setEditModal((prev) => ({
      ...prev,
      role: normalizedRole,
      max_discount_percent: isBillingRole(normalizedRole)
        ? isBillingRole(prev.role)
          ? prev.max_discount_percent
          : String(DEFAULT_BILLING_ROLE_DISCOUNT_CAP)
        : String(resolveStaffDefaultDiscountCap(normalizedRole)),
    }));
  };

  /* ---------------- Add form helpers ---------------- */
  const validate = () => {
    if (!name.trim()) return gentle("Name is required");
    if (!/^\d{10}$/.test(phone)) return gentle("Enter 10 digit phone number");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return gentle("Invalid email address");
    if (!password || password.length < 6)
      return gentle("Password must be at least 6 characters");
    if (!role) return gentle("Please select a role");
    if (!restaurantId) return gentle("Restaurant ID missing");
    if (!validateMaxDiscountPercent(role, maxDiscountPercent)) return false;
    return true;
  };

  const resetForm = () => {
    setName("");
    setPhone("");
    setEmail("");
    setPassword("");
    setRole("");
    setMaxDiscountPercent("");
    setShowPwd(false);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setSubmitting(true);
      if (isFullOfflineModeEnabled()) {
        await createOfflineStaff(restaurantId, {
          name,
          phone,
          email,
          password,
          role,
          max_discount_percent: buildStaffCapPayload(role, maxDiscountPercent),
          restaurantProfile: JSON.parse(localStorage.getItem("userData") || "{}").data || {},
        });
        toast.success("Staff added successfully");
        resetForm();
        await fetchStaff(restaurantId);
        return;
      }
      const res = await fetch(
        API_ENDPOINTS.ADD_STAFF,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: Number(restaurantId),
            name,
            phone,
            email,
            password,
            role,
            max_discount_percent: buildStaffCapPayload(role, maxDiscountPercent),
          }),
        }
      );
      const data = await res.json();

      if (res.ok && (data.success || data.status === true)) {
        toast.success("Staff added successfully");
        resetForm();
        fetchStaff(restaurantId); // refresh list
      } else {
        if (data?.errors && typeof data.errors === "object") {
          const messages = Object.values(data.errors).flat().join("\n");
          toast.error(messages || "Validation error");
        } else {
          toast.error(data?.message || "Failed to add staff");
        }
      }
    } catch (e) {
      console.error("Add staff error", e);
      toast.error(e?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------- Staff list (load/toggle/delete) ---------------- */
  const fetchStaff = useCallback(async (rid = restaurantId) => {
    const targetRestaurantId = typeof rid === "string" || typeof rid === "number"
      ? rid : restaurantId;
    if (!targetRestaurantId) return;
    setLoadingList(true);
    try {
      if (isFullOfflineModeEnabled()) {
        const json = await listOfflineStaff(targetRestaurantId);
        setStaff((Array.isArray(json?.data) ? json.data : []).map(normalizeStaffRow));
        return;
      }
      const res = await fetch(STAFF_LIST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: targetRestaurantId }),
      });
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setStaff(json.data.map((row) => normalizeStaffRow(row)));
      } else {
        toast.error(json.message || "Could not load staff list");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to fetch staff.");
    } finally {
      setLoadingList(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("userData"))?.data;
    if (u?.id) {
      setRestaurantId(u.id);
      fetchStaff(u.id);
    } else {
      toast.error("User not found. Please login again.");
      setLoadingList(false);
    }
  }, [fetchStaff]);

  const toggleActive = async (id, nextVal) => {
    try {
      // optimistic update
      setStaff((prev) =>
        prev.map((s) => (s.id === id ? { ...s, active: nextVal ? 1 : 0 } : s))
      );
      if (isFullOfflineModeEnabled()) {
        await toggleOfflineStaff(id, nextVal ? 1 : 0);
        return;
      }
      const res = await fetch(TOGGLE_STATUS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ staffid: id, active: nextVal ? 1 : 0 }),
      });
      const json = await res.json();
      if (!json.success) {
        // revert on failure
        setStaff((prev) =>
          prev.map((s) => (s.id === id ? { ...s, active: nextVal ? 0 : 1 } : s))
        );
        toast.error(json.message || "Could not update status");
      }
    } catch (e) {
      // revert on failure
      setStaff((prev) =>
        prev.map((s) => (s.id === id ? { ...s, active: nextVal ? 0 : 1 } : s))
      );
      console.error(e);
      toast.error(e?.message || "Failed to update status");
    }
  };

  const confirmDelete = (row) => {
    if (!window.confirm(`Delete staff "${row.name}"?`)) return;
    handleDelete(row.id);
  };

  const handleDelete = async (id) => {
    try {
      if (isFullOfflineModeEnabled()) {
        const json = await deleteOfflineStaff(id);
        setStaff((prev) => prev.filter((s) => s.id !== id));
        toast.success(json?.message || "Deleted");
        return;
      }
      const res = await fetch(DELETE_STAFF_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ staffid: id }),
      });
      const json = await res.json();
      if (json.success) {
        setStaff((prev) => prev.filter((s) => s.id !== id));
        toast.success("Deleted");
      } else {
        toast.error(json.message || "Could not delete staff");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to delete");
    }
  };

  /* ---------------- Edit staff ---------------- */
  const openEdit = (row) => {
    setEditModal({
      open: true,
      id: row.id,
      name: row.name || "",
      phone: row.phone || "",
      email: row.email || "",
      role: normalizeStaffRoleKey(row.role || ""),
      max_discount_percent: isBillingRole(row.role)
        ? formatStaffCapForInput(
            row.max_discount_percent ??
              resolveStaffDefaultDiscountCap(row.role)
          )
        : "0",
      password: "",
    });
    setShowEditPwd(false);
  };

  const validateEdit = () => {
    if (!editModal.name.trim()) return gentle("Name is required");
    if (!/^\d{10}$/.test(editModal.phone))
      return gentle("Enter 10 digit phone number");
    if (editModal.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editModal.email))
      return gentle("Invalid email address");
    if (editModal.password && editModal.password.length < 6)
      return gentle("New password must be at least 6 characters");
    if (
      !validateMaxDiscountPercent(
        editModal.role,
        editModal.max_discount_percent
      )
    ) {
      return false;
    }
    return true;
  };

  const saveEdit = async () => {
    if (!validateEdit()) return;
    try {
      setSavingEdit(true);
      if (isFullOfflineModeEnabled()) {
        await updateOfflineStaff(restaurantId, editModal.id, {
          name: editModal.name,
          phone: editModal.phone,
          email: editModal.email,
          role: editModal.role,
          max_discount_percent: buildStaffCapPayload(
            editModal.role,
            editModal.max_discount_percent
          ),
        });
        if (editModal.password) {
          await changeOfflineStaffPassword(
            restaurantId,
            editModal.id,
            editModal.password
          );
        }
        await fetchStaff(restaurantId);
        toast.success("Staff updated");
        setEditModal((m) => ({ ...m, open: false }));
        setShowEditPwd(false);
        return;
      }
      const res = await fetch(UPDATE_STAFF_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          staffid: editModal.id,
          restaurantid: restaurantId,
          name: editModal.name,
          phone: editModal.phone,
          email: editModal.email,
          role: editModal.role,
          max_discount_percent: buildStaffCapPayload(
            editModal.role,
            editModal.max_discount_percent
          ),
        }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false) {
        throw new Error(json?.message || "Failed to update staff");
      }
      // merge locally
      setStaff((prev) =>
        prev.map((s) =>
          s.id === editModal.id
            ? {
                ...s,
                name: editModal.name,
                phone: editModal.phone,
                email: editModal.email,
                role: editModal.role,
                max_discount_percent: buildStaffCapPayload(
                  editModal.role,
                  editModal.max_discount_percent
                ),
              }
            : s
        )
      );
      if (editModal.password) {
        const passwordRes = await fetch(CHANGE_PASSWORD_URL, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId, staffid: editModal.id, new_password: editModal.password }),
        });
        const passwordJson = await passwordRes.json().catch(() => ({}));
        if (!passwordRes.ok || passwordJson?.success === false) {
          throw new Error(passwordJson?.message || "Profile updated, but password could not be changed");
        }
      }
      toast.success("Staff updated");
      setEditModal((m) => ({ ...m, open: false }));
      setShowEditPwd(false);
    } catch (e) {
      console.error("Edit staff error", e);
      toast.error(e.message || "Unable to update staff");
    } finally {
      setSavingEdit(false);
    }
  };

  const forceLogout = async (row) => {
    if (!window.confirm(`Log out ${row.name || "this staff member"} from all devices?`)) return;
    try {
      if (isFullOfflineModeEnabled()) {
        const json = await forceLogoutOfflineStaff(restaurantId, row.id);
        setStaff((prev) => prev.map((item) => item.id === row.id ? { ...item, active_session_count: 0, has_active_session: false } : item));
        toast.success(json?.message || "Staff logged out from all devices");
        return;
      }
      const res = await fetch(FORCE_LOGOUT_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ restaurantid: restaurantId, staffid: row.id }) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) throw new Error(json?.message || "Force logout failed");
      setStaff((prev) => prev.map((item) => item.id === row.id ? { ...item, active_session_count: 0, has_active_session: false } : item));
      toast.success(json?.message || "Staff logged out from all devices");
    } catch (e) { toast.error(e.message); }
  };

  /* ---------------- filtered staff list ---------------- */
  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    const normalizedFilterRole = normalizeStaffRoleKey(filterRole);
    return staff.filter((row) => {
      const matchRole = normalizedFilterRole
        ? normalizeStaffRoleKey(row.role) === normalizedFilterRole
        : true;
      if (!q) return matchRole;
      const hay = `${row.name || ""} ${row.phone || ""} ${row.email || ""} ${
        normalizeStaffRoleKey(row.role) || ""
      }`.toLowerCase();
      return matchRole && hay.includes(q);
    });
  }, [staff, search, filterRole]);

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={3000} />
      <Wrap>
        {/* ---------- Add Staff Card ---------- */}
        <Card>
          <Header>
            <Title>Add Staff</Title>
            {/* <Sub>Cashier / Waiter / Chef ko jaldi se add karein</Sub> */}
          </Header>

          <Form>
            <Field>
              <Label>Name</Label>
              <Input
                placeholder="Enter full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
              />
            </Field>

            <Field>
              <Label>Phone</Label>
              <Input
                placeholder="10 digit phone"
                value={phone}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
                  setPhone(v);
                }}
                inputMode="numeric"
                disabled={submitting}
              />
            </Field>

            <Field>
              <Label>Email (optional)</Label>
              <Input
                placeholder="example@mail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                autoComplete="email"
                disabled={submitting}
              />
            </Field>

            <Field style={{ gridColumn: "span 1" }}>
              <Label>Password</Label>
              <PwdWrap>
                <Input
                  placeholder="Set a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPwd ? "text" : "password"}
                  disabled={submitting}
                />
                <PwdBtn
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  disabled={submitting}
                >
                  {showPwd ? <FaEyeSlash /> : <FaEye />}
                </PwdBtn>
              </PwdWrap>
              <Hint>At least 6 characters</Hint>
            </Field>

            <Field>
              <Label>Role</Label>
              <SelectWrap>
                <Select
                  value={role}
                  onChange={(e) => updateAddRole(e.target.value)}
                  disabled={submitting}
                >
                  <option value="">Select role</option>
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Select>
                <SelIcon>
                  <FaChevronDown />
                </SelIcon>
              </SelectWrap>
            </Field>

            {isBillingRole(role) ? (
              <Field>
                <Label>Max Discount %</Label>
                <Input
                  placeholder="Enter max discount percent"
                  value={maxDiscountPercent}
                  onChange={(e) =>
                    setMaxDiscountPercent(sanitizePercentInput(e.target.value))
                  }
                  inputMode="decimal"
                  disabled={submitting}
                />
                <Hint>Staff cannot apply discount above this percentage.</Hint>
              </Field>
            ) : null}

            <Actions>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !restaurantId}
              >
                {submitting ? "Saving..." : "Save"}
              </Button>
              <Ghost onClick={resetForm} disabled={submitting}>
                Reset
              </Ghost>
            </Actions>
          </Form>
        </Card>

        {/* ---------- Staff List Card ---------- */}
        <Card style={{ marginTop: 16, width: "100%" }}>
         <ListHeader
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  }}
>
  {/* Left Side */}
  <div>
    <h3 style={{ margin: 0, fontSize: 16, color: COLOR.text }}>
      Staff List
    </h3>
    <div
      style={{
        fontSize: 12,
        color: COLOR.muted,
      }}
    >
      Total: {staff.length}
    </div>
  </div>

  {/* Right Side Controls */}
  <ListControls
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}
  >
    <SearchInput
      placeholder="Search by name / phone / email"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
    />

    <SmallSelectWrap>
      <SmallSelect
        value={filterRole}
        onChange={(e) => setFilterRole(e.target.value)}
      >
        <option value="">All roles</option>
        {ROLE_OPTIONS.map((r) => (
          <option key={r.value} value={r.value}>
            {r.label}
          </option>
        ))}
      </SmallSelect>
    </SmallSelectWrap>

    <ReloadBtn onClick={fetchStaff}>
      <FaSyncAlt />
      <span>Reload</span>
    </ReloadBtn>
  </ListControls>
</ListHeader>


          {loadingList ? (
            <Loading>Loading…</Loading>
          ) : filteredStaff.length === 0 ? (
            <Empty> No staff found. </Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: "34%" }}>Name</Th>
                  <Th style={{ width: "18%" }}>Role</Th>
                  <Th style={{ width: "16%" }}>Active</Th>
                  <Th style={{ width: "32%" }}>Action</Th>
                </tr>
              </thead>
              <tbody>
                {filteredStaff.map((row) => {
                  const isActive =
                    row.active === 1 ||
                    row.active === "1" ||
                    row.active === true;
                  return (
                    <tr key={row.id}>
                      <Td>
                        <strong>{row.name || "-"}</strong>
                        <RowSub>
                          {row.phone ? <span>{row.phone}</span> : null}
                          {row.email ? <span>{row.email}</span> : null}
                          {isBillingRole(row.role) ? (
                            <span>
                              Max Discount:{" "}
                              {formatDiscountPercent(
                                row.max_discount_percent || 0
                              )}
                              %
                            </span>
                          ) : null}
                        </RowSub>
                      </Td>
                      <Td>
                        {row.role ? (
                          <RoleBadge>
                            {capitalize(normalizeStaffRoleKey(row.role))}
                          </RoleBadge>
                        ) : (
                          <Muted>—</Muted>
                        )}
                      </Td>
                      <Td>
                        <ToggleWrap>
                          <input
                            id={`t-${row.id}`}
                            type="checkbox"
                            checked={isActive}
                            onChange={(e) =>
                              toggleActive(row.id, e.target.checked)
                            }
                          />
                          <label htmlFor={`t-${row.id}`}></label>
                        </ToggleWrap>
                      </Td>
                      <Td>
                        <ActionRow>
                          <GhostBtn onClick={() => openEdit(row)}>
                            <FaEdit /> Edit
                          </GhostBtn>
                          <Danger onClick={() => confirmDelete(row)}>
                            <FaTrash /> Delete
                          </Danger>
                          <GhostBtn onClick={() => forceLogout(row)}>Logout devices</GhostBtn>
                        </ActionRow>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>
      </Wrap>

      {editModal.open && (
        <Modal
          onMouseDown={(e) =>
            e.target === e.currentTarget &&
            setEditModal((m) => ({ ...m, open: false }))
          }
        >
          <ModalCard onMouseDown={(e) => e.stopPropagation()}>
            <ModalHead>
              <div>
                <ModalTitle>Edit Staff</ModalTitle>
                <ModalSub>Update profile and set a new login password</ModalSub>
              </div>
              <CloseBtn
                onClick={() => setEditModal((m) => ({ ...m, open: false }))}
              >
                <FaTimes />
              </CloseBtn>
            </ModalHead>
            <Form>
              <Field>
                <Label>Name</Label>
                <Input
                  value={editModal.name}
                  onChange={(e) =>
                    setEditModal((m) => ({ ...m, name: e.target.value }))
                  }
                  placeholder="Full name"
                  disabled={savingEdit}
                />
              </Field>
              <Field>
                <Label>Phone</Label>
                <Input
                  value={editModal.phone}
                  onChange={(e) =>
                    setEditModal((m) => ({
                      ...m,
                      phone: e.target.value.replace(/[^\d]/g, "").slice(0, 10),
                    }))
                  }
                  placeholder="10 digit phone"
                  inputMode="numeric"
                  disabled={savingEdit}
                />
              </Field>
              <Field>
                <Label>Email</Label>
                <Input
                  value={editModal.email}
                  onChange={(e) =>
                    setEditModal((m) => ({ ...m, email: e.target.value }))
                  }
                  placeholder="Email"
                  type="email"
                  autoComplete="email"
                  disabled={savingEdit}
                />
              </Field>
              <Field>
                <Label>Role</Label>
                <Select
                  value={editModal.role}
                  onChange={(e) => updateEditRole(e.target.value)}
                  disabled={savingEdit}
                >
                  {ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </Select>
              </Field>
              {isBillingRole(editModal.role) ? (
                <Field>
                  <Label>Max Discount %</Label>
                  <Input
                    value={editModal.max_discount_percent}
                    onChange={(e) =>
                      setEditModal((m) => ({
                        ...m,
                        max_discount_percent: sanitizePercentInput(
                          e.target.value
                        ),
                      }))
                    }
                    placeholder="Enter max discount percent"
                    inputMode="decimal"
                    disabled={savingEdit}
                  />
                  <Hint>Staff cannot apply discount above this percentage.</Hint>
                </Field>
              ) : null}
              <Field>
                <Label>Set New Password (optional)</Label>
                <PwdWrap>
                  <Input
                    value={editModal.password}
                    onChange={(e) =>
                      setEditModal((m) => ({ ...m, password: e.target.value }))
                    }
                    placeholder="Enter new password"
                    type={showEditPwd ? "text" : "password"}
                    disabled={savingEdit}
                  />
                  <PwdBtn
                    type="button"
                    onClick={() => setShowEditPwd((s) => !s)}
                    disabled={savingEdit}
                  >
                    {showEditPwd ? <FaEyeSlash /> : <FaEye />}
                  </PwdBtn>
                </PwdWrap>
                <Hint>Leave blank to keep existing password</Hint>
              </Field>
            </Form>
            <ModalActions>
              <Ghost
                onClick={() => setEditModal((m) => ({ ...m, open: false }))}
                disabled={savingEdit}
              >
                Cancel
              </Ghost>
              <Button type="button" onClick={saveEdit} disabled={savingEdit}>
                {savingEdit ? "Saving..." : "Save"}
              </Button>
            </ModalActions>
          </ModalCard>
        </Modal>
      )}
    </Page>
  );
};

export default AddWaiterPage;

/* ================= utils ================= */
const capitalize = (s) =>
  typeof s === "string" && s ? s[0].toUpperCase() + s.slice(1) : s;

/* ================= styled ================= */
const Page = styled.div`
  min-height: 100vh;
  background: #f6f7fb;
  display: block;
  padding: 28px 16px;
  font-family: "Sora", "Plus Jakarta Sans", Inter, system-ui, Segoe UI, Roboto,
    "Helvetica Neue", Arial;
`;
const Wrap = styled.div`
  width: 100%;
  max-width: none;
`;
const Card = styled.div`
  background: ${COLOR.white};
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.08);
  padding: 22px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
`;
const Form = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 1;
`;
const Label = styled.label`
  font-size: 13px;
  font-weight: 700;
  color: ${COLOR.primary};
  margin-bottom: 6px;
`;
const Input = styled.input`
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #ffffff;
  color: ${COLOR.text};
  border-radius: 12px;
  padding: 12px 12px;
  font-size: 14px;
  outline: none;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
  &:focus {
    border-color: ${COLOR.primary};
    box-shadow: 0 0 0 3px rgba(var(--app-primary-rgb), 0.14);
  }
`;
const PwdWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;
const PwdBtn = styled.button`
  position: absolute;
  right: 10px;
  background: transparent;
  border: 0;
  color: #5b6470;
  cursor: pointer;
  padding: 4px;
  display: grid;
  place-items: center;
  svg {
    font-size: 16px;
  }
`;
const Hint = styled.div`
  color: ${COLOR.muted};
  font-size: 12px;
  margin-top: 4px;
`;
const SelectWrap = styled.div`
  position: relative;
`;
const Select = styled.select`
  appearance: none;
  width: 100%;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #ffffff;
  color: ${COLOR.text};
  border-radius: 12px;
  padding: 12px 38px 12px 12px;
  font-size: 14px;
  outline: none;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
  &:focus {
    border-color: ${COLOR.primary};
    box-shadow: 0 0 0 3px rgba(var(--app-primary-rgb), 0.14);
  }
`;
const SelIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  translate: 0 -50%;
  color: #5b6470;
  pointer-events: none;
`;
const Actions = styled.div`
  grid-column: span 2;
  display: flex;
  gap: 10px;
  margin-top: 6px;
  @media (max-width: 720px) {
    grid-column: span 1;
    flex-direction: column;
  }
`;
const Button = styled.button`
  flex: 1;
  background: linear-gradient(135deg, var(--app-primary), var(--app-primary-hover));
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 12px 16px;
  font-weight: 900;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(var(--app-primary-rgb), 0.25);
  transition: transform 0.05s;
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  &:active {
    transform: translateY(1px);
  }
`;
const Ghost = styled.button`
  background: #ffffff;
  color: ${COLOR.primary};
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 12px;
  padding: 12px 16px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

/* ----- List styles ----- */
const ListHeader = styled.div`
  margin-bottom: 10px;
`;
const ListControls = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
`;
const ReloadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  color: ${COLOR.text};
  background: #fff;
  padding: 8px 12px;
  border-radius: 12px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
`;

const SearchInput = styled.input`
  min-width: 220px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  padding: 8px 12px;
  font-size: 13px;
  background: #fff;
  outline: none;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
  &:focus {
    border-color: ${COLOR.primary};
    box-shadow: 0 0 0 2px rgba(var(--app-primary-rgb), 0.15);
  }

  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }
`;

const SmallSelectWrap = styled.div`
  position: relative;
`;
const SmallSelect = styled.select`
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  padding: 8px 12px;
  font-size: 13px;
  background: #fff;
  outline: none;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
`;

const Loading = styled.div`
  padding: 24px;
  text-align: center;
  color: ${COLOR.muted};
`;
const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: ${COLOR.muted};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
  thead {
    background: #f1f5f9;
  }
  th,
  td {
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    padding: 10px;
    vertical-align: middle;
  }
  tbody tr:hover {
    background: #f8fafc;
  }
`;
const Th = styled.th`
  text-align: left;
  color: ${COLOR.text};
  font-weight: 800;
`;
const Td = styled.td`
  color: ${COLOR.text};
`;
const RowSub = styled.div`
  margin-top: 4px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  color: #5f6b76;
  font-size: 12px;
  span {
    background: #f2f7fd;
    padding: 2px 6px;
    border-radius: 6px;
  }
`;
const Muted = styled.span`
  color: #9aa0a6;
  font-style: italic;
`;
const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
const GhostBtn = styled(Ghost)`
  border-color: ${COLOR.primary};
  color: ${COLOR.primary};
  background: ${COLOR.white};
  padding: 8px 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
`;
const RoleBadge = styled.span`
  display: inline-block;
  background: #eef2ff;
  color: #4338ca;
  padding: 6px 10px;
  font-weight: 800;
  border-radius: 999px;
  border: 1px solid rgba(67, 56, 202, 0.2);
`;

const Danger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${COLOR.danger};
  color: ${COLOR.danger};
  background: #fff5f5;
  padding: 8px 12px;
  border-radius: 12px;
  font-weight: 800;
  cursor: pointer;
`;

/* Pretty toggle */
const ToggleWrap = styled.div`
  position: relative;
  height: 28px;
  input {
    position: absolute;
    opacity: 0;
  }
  label {
    display: block;
    width: 50px;
    height: 28px;
    background: #d1dae4;
    border-radius: 999px;
    cursor: pointer;
    position: relative;
    transition: background 0.2s ease;
  }
  label:after {
    content: "";
    position: absolute;
    top: 3px;
    left: 3px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    transition: left 0.2s ease;
  }
  input:checked + label {
    background: ${COLOR.primary};
  }
  input:checked + label:after {
    left: 25px;
  }
`;

/* -------- Edit modal -------- */
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: grid;
  place-items: center;
  padding: 18px;
  z-index: 999;
`;
const ModalCard = styled.div`
  width: 100%;
  max-width: 520px;
  background: #fff;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.22);
`;
const ModalHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;
const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: ${COLOR.text};
`;
const ModalSub = styled.div`
  color: ${COLOR.muted};
  font-size: 13px;
  margin-top: 2px;
  margin-bottom: 12px;
`;
const CloseBtn = styled.button`
  border: none;
  background: #f1f5f9;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  cursor: pointer;
`;
const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
`;
